//src/components/notification/NotificationList.tsx
import React, { useEffect, useCallback, useRef } from "react";
import {
    NotificationConnection,
    Notification,
} from "../../gql/types";
import styles from "./NotificationList.module.css";
import Spinner from "../button/Spinner";
import NotificationUnit from "./NotificationUnit";

interface NotificationListProps {
    connection: NotificationConnection;
    loading?: boolean;
    isPaginated?: boolean;
    loadMore?: (cursor: string | null | undefined) => Promise<void>;
    emptyState?: React.ReactNode;
    onClickNotification?: (notification: Notification) => void;
}

const NotificationList: React.FC<NotificationListProps> = ({
    connection,
    loading = false,
    isPaginated = false,
    loadMore,
    emptyState,
    onClickNotification,
}) => {
    const { edges, pageInfo } = connection;
    const hasNotifications = edges && edges.length > 0;
    const sentinelRef = useRef<HTMLDivElement>(null);

    //Triggers loadMore when the sentinel element becomes visible
    const handleLoadMore = useCallback(() => {
        if (isPaginated && loadMore && pageInfo?.hasNextPage && !loading) {
            loadMore(pageInfo.endCursor);
        }
    }, [isPaginated, loadMore, pageInfo, loading]);

    //Set up the IntersectionObserver for infinite scrolling
    useEffect(() => {
        if (!isPaginated || !sentinelRef.current || !pageInfo?.hasNextPage) return;
        
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting) handleLoadMore();
            },
            { threshold: 1.0 } 
        );

        observer.observe(sentinelRef.current);
        return () => observer.disconnect();
    }, [handleLoadMore, isPaginated, pageInfo]);

    if (!hasNotifications && !loading) {
        return (
            <div className = {styles.emptyStateWrapper}>
                <div className={styles.emptyState}>
                    {emptyState || <p className={styles.emptyStateText}>We'll let you know when something comes up!</p>}
                </div>
            </div>
        );
    }

    return (
        <div className={styles.notificationListContainer}>
            {edges.map((edge, index) => (
                <div
                    key={edge.node.id}
                    className={`${styles.borderWrapper} ${index === 0 ? styles.topBorder : ''}`} 
                >
                    <NotificationUnit 
                        notification={edge.node} 
                        onClick={onClickNotification}
                    />
                </div>
            ))}
            
            {loading && edges.length === 0 && (
                <div className={styles.loadingContainer}>
                    <Spinner />
                </div>
            )}

            {isPaginated && pageInfo?.hasNextPage && (
                <div ref={sentinelRef} className={styles.sentinel}>
                    {loading && <Spinner />}
                </div>
            )}
            <div className={styles.notificationEndPadder}></div>
        </div>
    );
};

export default NotificationList;